'use client';

import * as Styled from './WorkspaceSelector.styled';
import { useCanCreateWorkspace } from '@modules/workspaces/hooks/useCanCreateWorkspace';
import WorkspaceSelectModal from 'app/(authenticated)/_components/WorkspaceSelect/WorkspaceSelect';
import WorkspaceIcon from 'app/_components/Icons/WorkspaceIcon';
import { useWorkspace, WorkspaceWithPathName } from 'app/_providers/WorkspaceProvider';

import { Divider } from '@capra/core';
import { Dropdown } from 'antd';
import truncate from 'lodash/truncate';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { AccountState, workspaceDeletingStates } from 'shared/src/account/types';

import type { MenuProps } from 'antd';

const WORKSPACE_NAME_MAX_DISPLAY_LENGTH = 64;
const truncateWorkspaceName = (workspaceName: string): string =>
  truncate(workspaceName, { length: WORKSPACE_NAME_MAX_DISPLAY_LENGTH });

interface ICreateWorkspaceMenuItemsProps {
  workspaces: WorkspaceWithPathName[];
  currentWorkspaceName: string | undefined;
  handleRedirect: (pathname: string) => void;
  closeDropdown: () => void;
}

const createWorkspaceMenuItems = ({
  workspaces,
  currentWorkspaceName,
  handleRedirect,
  closeDropdown,
}: ICreateWorkspaceMenuItemsProps): MenuProps['items'] => workspaces.map((workspace) => {
  const displayName = truncateWorkspaceName(workspace.alias || workspace.name);
  const isCurrentWorkspace = workspace.name === currentWorkspaceName;

  const handleClick = (): void => {
    closeDropdown();
    handleRedirect(workspace.pathname as string);
  };

  const getWorkspaceItemLabel = () => {
    if (workspaceDeletingStates.includes(workspace.state as AccountState)) {
      return <Styled.InactiveWorkspaceItem disabled>{displayName}</Styled.InactiveWorkspaceItem>;
    }

    return isCurrentWorkspace
      ? <Styled.CurrentWorkspaceItem onClick={handleClick}>{displayName}</Styled.CurrentWorkspaceItem>
      : <Styled.ActiveSelectorItem onClick={handleClick}>{displayName}</Styled.ActiveSelectorItem>;
  };

  return {
    key: workspace.name,
    label: getWorkspaceItemLabel(),
  };
});

const DropdownRender = (node: React.ReactNode) => (
  <Styled.DropdownScrollableMenu>{node}</Styled.DropdownScrollableMenu>
);

interface CurrentWorkspaceProps {
  workspace: string;
  onClick?: () => void;
}

const CurrentWorkspace: React.FC<CurrentWorkspaceProps> = ({ workspace, onClick }) => (
  <Styled.WorkspaceInfoWrap>
    {(className) => (
      <Styled.WorkspaceInfo className={className} onClick={onClick}>
        <Styled.IconWrap className={className}>
          <WorkspaceIcon className={className} />
        </Styled.IconWrap>

        <Styled.WorkspaceName className={className}>{workspace}</Styled.WorkspaceName>
      </Styled.WorkspaceInfo>
    )}
  </Styled.WorkspaceInfoWrap>
);

interface WorkspaceSelectorProps {
  isOEM: boolean;
}

export const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({ isOEM }) => {
  const router = useRouter();
  const { name, alias, workspaces } = useWorkspace();
  const { canCreateWorkspaces, maxWorkspaces } = useCanCreateWorkspace();
  const [showManageWorkspacesModal, setShowManageWorkspacesModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const pathname = usePathname();
  const canCreateWorkspace = canCreateWorkspaces && workspaces.length < maxWorkspaces;
  const hasMultipleWorkspaces = workspaces.length > 1;

  const showDropdown = canCreateWorkspace || hasMultipleWorkspaces;

  const currentWorkspaceDisplayName = alias || name;
  const processedCurrentWorkspaceDisplayName = truncateWorkspaceName(currentWorkspaceDisplayName);

  const sortedWorkspaces = [...workspaces].sort((a, b) => {
    const isCurrentA = a.name === name;
    const isCurrentB = b.name === name;

    if (isCurrentA) {
      return -1;
    }

    if (isCurrentB) {
      return 1;
    }

    return 0;
  });

  useEffect(() => {
    setDropdownOpen(false);
    setShowManageWorkspacesModal(false);
  }, [pathname]);

  const handleCloseModal = (): void => {
    setShowManageWorkspacesModal(false);
  };

  const handleOpenManageWorkspaces = (e?: React.MouseEvent): void => {
    e?.stopPropagation();
    setShowManageWorkspacesModal(true);
  };

  const handleDirectClick = (): void => {
    setShowManageWorkspacesModal(true);
  };

  const handleRedirect = (pathname: string): void => {
    router.push(pathname);
  };

  const handleDropdownOpenChange = (open: boolean): void => {
    if (!showManageWorkspacesModal) {
      setDropdownOpen(open);
    }
  };

  const closeDropdown = (): void => {
    setDropdownOpen(false);
  };

  const defaultSelectorItems: NonNullable<MenuProps['items']> = [
    {
      key: 'manage-workspaces',
      label: (
        <Styled.ManageWorkspacesItem onClick={handleOpenManageWorkspaces}>
          Manage Workspaces
        </Styled.ManageWorkspacesItem>
      ),
    },
    {
      type: 'divider',
    },
  ];

  const workspaceMenuItems = createWorkspaceMenuItems({
    workspaces: sortedWorkspaces,
    currentWorkspaceName: name,
    handleRedirect,
    closeDropdown,
  }) ?? [];

  const items = [...defaultSelectorItems, ...workspaceMenuItems];

  if (isOEM) {
    return null;
  }

  return (
    <Styled.WorkspaceSelectorWrapper>
      <Divider type='vertical' />

      <Styled.WorkspaceSwitcher>
        {showDropdown
          ? (
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              placement='bottomLeft'
              popupRender={DropdownRender}
              open={dropdownOpen}
              onOpenChange={handleDropdownOpenChange}
            >
              <Styled.CurrentItemWrapper>
                <CurrentWorkspace workspace={processedCurrentWorkspaceDisplayName} />
              </Styled.CurrentItemWrapper>
            </Dropdown>
          )
          : (
            <Styled.CurrentItemWrapper>
              <CurrentWorkspace
                workspace={processedCurrentWorkspaceDisplayName}
                onClick={handleDirectClick}
              />
            </Styled.CurrentItemWrapper>
          )}
      </Styled.WorkspaceSwitcher>

      {showManageWorkspacesModal && <WorkspaceSelectModal closeModal={handleCloseModal} />}
    </Styled.WorkspaceSelectorWrapper>
  );
};
